type RGB = { r: number; g: number; b: number };

export const Default_Background_Primary_Color = "8DA9B0";
export const Default_Background_Secondary_Color = "381f69";
export const Default_Button_Background_Color = "2C2679";
export const Default_Button_Text_Color = "ffffff";
export const Default_Instructor_Color = "699DCEAB";

// The accent every studio had before Design Studio: the purple → pink gradient of the
// "accent" button, the selected slot and the switches. A studio without an accent of its
// own keeps it.
export const Default_Accent_From = "7877c6";
export const Default_Accent_To = "ff77c6";
export const Default_Accent_Text = "ffffff";

/** Dark text for light fills: the e-mail design system's ink (HeaderPaletteUtility.DarkText). */
export const Dark_Text_Color = "15102b";

/** The studio colours that decide the accent. Colours are stored as six hex digits, no '#'. */
export interface IStudioAccentSource {
  backgroundColorPrimary?: string | null;
  backgroundColorSecondary?: string | null;
  accentColorFrom?: string | null;
  accentColorTo?: string | null;
  accentTextColor?: string | null;
}

/** A resolved accent, every colour '#'-prefixed and ready for CSS. */
export interface IThemeAccent {
  from: string;
  to: string;
  text: string;
}

export const generateHexFromString = (color: string) => {
  return `#${color}`;
};

export const darkenHex = (hex: string | undefined, amount: number): string => {
  if (!hex) {
    return "";
  }
  hex = hex.replace("#", "");
  const r = Math.max(0, parseInt(hex.substring(0, 2), 16) - amount);
  const g = Math.max(0, parseInt(hex.substring(2, 4), 16) - amount);
  const b = Math.max(0, parseInt(hex.substring(4, 6), 16) - amount);

  return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
};

export const hexToRgb = (hex: string): RGB => {
  hex = hex.replace("#", "");
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
};

export const rgbToHex = (r: number, g: number, b: number) => {
  return (
    "#" +
    [r, g, b]
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("")
      .toLowerCase()
  );
};

export const mixColors = (c1: RGB, c2: RGB, ratio: number): RGB => {
  return {
    r: Math.round(c1.r + (c2.r - c1.r) * ratio),
    g: Math.round(c1.g + (c2.g - c1.g) * ratio),
    b: Math.round(c1.b + (c2.b - c1.b) * ratio),
  };
};

const withHash = (hex: string) => (hex.startsWith("#") ? hex : `#${hex}`);

export const isValidHexColor = (value: string | null | undefined): value is string =>
  !!value && /^#?[0-9A-Fa-f]{6}$/.test(value.trim());

/** WCAG 2 relative luminance, the same measure readableText() and the e-mails use. */
export const relativeLuminance = (hex: string): number => {
  const { r, g, b } = hexToRgb(hex);
  const toLinear = (channel: number) => {
    const v = channel / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

export const contrastRatio = (a: string, b: string): number => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);

  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/** CSS `color-mix(in srgb, a, b weightOfB)`, as public/styles.css mixes the canvas. */
export const mixHex = (a: string, b: string, weightOfB: number): string => {
  const { r, g, b: blue } = mixColors(hexToRgb(a), hexToRgb(b), weightOfB);
  return rgbToHex(r, g, blue);
};

export const lightenHex = (hex: string, amount: number): string => {
  const { r, g, b } = hexToRgb(hex);
  const up = (channel: number) => Math.min(255, channel + amount);
  return rgbToHex(up(r), up(g), up(b));
};

/** "r, g, b", so SCSS can write rgba(var(--accent-from-rgb, 120, 119, 198), 0.25). */
export const hexToRgbTriplet = (hex: string): string => {
  const { r, g, b } = hexToRgb(hex);
  return `${r}, ${g}, ${b}`;
};

/**
 * The LIGHTEST dark surface accent-coloured text sits on: the active menu row (10% white
 * over the sidebar glass). The worst case for light text, so text that reads here reads
 * on every darker panel too.
 */
export const Accent_On_Dark_Surface = "#33324a";

/**
 * The colour itself when it already reads as text on the dark app (WCAG AA), otherwise
 * the same colour mixed toward white just far enough to read. For accents used as text
 * or icon colour (the active menu item), where a deep wine or navy would vanish.
 */
export const legibleOnDark = (hex: string): string => {
  const base = withHash(hex);

  for (let step = 0; step <= 20; step++) {
    const candidate = mixHex(base, "#ffffff", step / 20);
    if (contrastRatio(candidate, Accent_On_Dark_Surface) >= 4.5) {
      return candidate;
    }
  }

  return "#ffffff";
};

/**
 * A selected-card tint of an accent stop that white card text still reads on: the paler
 * the accent, the fainter the tint, since a pale tint lightens the dark card fastest.
 */
export const accentTint = (hex: string): string => {
  const luminance = relativeLuminance(hex);
  const alpha = luminance > 0.6 ? 0.07 : luminance > 0.3 ? 0.1 : 0.15;
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** White or ink, whichever reads better on a solid fill. Returned the way studios store colours: no '#'. */
export const bestTextColor = (background: string): string =>
  contrastRatio(background, Default_Button_Text_Color) >=
  contrastRatio(background, Dark_Text_Color)
    ? Default_Button_Text_Color
    : Dark_Text_Color;

/**
 * White or ink for a two-stop gradient: the one whose WORSE stop reads better, since the
 * label crosses both. Returned without '#'. Mirrors StudioService's server-side default.
 */
export const bestGradientTextColor = (from: string, to: string): string => {
  const worst = (text: string) =>
    Math.min(contrastRatio(from, text), contrastRatio(to, text));

  return worst(Default_Button_Text_Color) >= worst(Dark_Text_Color)
    ? Default_Button_Text_Color
    : Dark_Text_Color;
};

/**
 * The studio's own accent, or null when it never chose one (then today's purple → pink
 * stays, exactly as before Design Studio). A missing text colour is derived.
 */
export const getStudioAccent = (
  studio: IStudioAccentSource | null | undefined,
): IThemeAccent | null => {
  const from = studio?.accentColorFrom?.trim();
  const to = studio?.accentColorTo?.trim();

  if (!isValidHexColor(from) || !isValidHexColor(to)) {
    return null;
  }

  const text = isValidHexColor(studio?.accentTextColor)
    ? studio!.accentTextColor!.trim()
    : bestGradientTextColor(from, to);

  return { from: withHash(from), to: withHash(to), text: withHash(text) };
};

const ACCENT_PROPERTIES = [
  "--accent-from",
  "--accent-to",
  "--accent-text",
  "--accent-from-hover",
  "--accent-to-hover",
  "--accent-from-rgb",
  "--accent-to-rgb",
  "--accent-on-dark",
  "--accent-from-on-dark",
  "--accent-tint-from",
  "--accent-tint-to",
];

const WIZARD_ACCENT_PROPERTIES = [
  "--wizard-accent-primary",
  "--wizard-accent-primary-hover",
  "--wizard-accent-secondary",
  "--wizard-accent-secondary-hover",
  "--wizard-accent-primary-alpha-20",
  "--wizard-accent-primary-alpha-15",
  "--wizard-accent-primary-alpha-10",
  "--wizard-accent-secondary-alpha-20",
  "--wizard-accent-secondary-alpha-15",
  "--wizard-accent-secondary-alpha-10",
];

/**
 * The --wizard-accent-* family the booking screens read. The alpha steps are the ones
 * the portal wizards have always used (the names do not match the values; consumers
 * depend on the values).
 */
export const setWizardAccentProperties = (primary: string, secondary: string) => {
  const root = document.documentElement;
  const primaryHex = withHash(primary);
  const secondaryHex = withHash(secondary);
  const p = hexToRgb(primaryHex);
  const s = hexToRgb(secondaryHex);

  root.style.setProperty("--wizard-accent-primary", primaryHex);
  root.style.setProperty("--wizard-accent-primary-hover", darkenHex(primaryHex, 30));
  root.style.setProperty("--wizard-accent-secondary", secondaryHex);
  root.style.setProperty("--wizard-accent-secondary-hover", darkenHex(secondaryHex, 30));
  root.style.setProperty("--wizard-accent-primary-alpha-20", `rgba(${p.r}, ${p.g}, ${p.b}, 0.4)`);
  root.style.setProperty("--wizard-accent-primary-alpha-15", `rgba(${p.r}, ${p.g}, ${p.b}, 0.35)`);
  root.style.setProperty("--wizard-accent-primary-alpha-10", `rgba(${p.r}, ${p.g}, ${p.b}, 0.25)`);
  root.style.setProperty("--wizard-accent-secondary-alpha-20", `rgba(${s.r}, ${s.g}, ${s.b}, 0.45)`);
  root.style.setProperty("--wizard-accent-secondary-alpha-15", `rgba(${s.r}, ${s.g}, ${s.b}, 0.35)`);
  root.style.setProperty("--wizard-accent-secondary-alpha-10", `rgba(${s.r}, ${s.g}, ${s.b}, 0.25)`);
};

/**
 * What the portal booking wizards paint their accents with: the studio's accent when it
 * has one (the gradient runs secondary → primary, so the start is the secondary), else
 * its background colours, as they always did.
 */
export const applyStudioWizardAccent = (
  studio: IStudioAccentSource | null | undefined,
) => {
  const accent = getStudioAccent(studio);

  if (accent) {
    setWizardAccentProperties(accent.to, accent.from);
    return;
  }

  setWizardAccentProperties(
    generateHexFromString(studio?.backgroundColorPrimary ?? Default_Background_Primary_Color),
    generateHexFromString(studio?.backgroundColorSecondary ?? Default_Background_Secondary_Color),
  );
};

// True while the --wizard-accent-* values on :root came from a studio accent (set below)
// rather than from a portal wizard's legacy background colours. Lets a studio without an
// accent take them back without wiping values a wizard set on purpose.
let themeOwnsWizardAccent = false;

const applyAccentColors = (accent: IThemeAccent | null | undefined) => {
  const root = document.documentElement;

  if (!accent) {
    ACCENT_PROPERTIES.forEach((name) => root.style.removeProperty(name));

    if (themeOwnsWizardAccent) {
      WIZARD_ACCENT_PROPERTIES.forEach((name) => root.style.removeProperty(name));
      themeOwnsWizardAccent = false;
    }

    return;
  }

  const from = withHash(accent.from);
  const to = withHash(accent.to);

  root.style.setProperty("--accent-from", from);
  root.style.setProperty("--accent-to", to);
  root.style.setProperty("--accent-text", withHash(accent.text));
  root.style.setProperty("--accent-from-hover", lightenHex(from, 18));
  root.style.setProperty("--accent-to-hover", lightenHex(to, 18));
  root.style.setProperty("--accent-from-rgb", hexToRgbTriplet(from));
  root.style.setProperty("--accent-to-rgb", hexToRgbTriplet(to));
  root.style.setProperty("--accent-on-dark", legibleOnDark(to));
  // The start stop as text, for the white → accent headings that end on the start colour.
  root.style.setProperty("--accent-from-on-dark", legibleOnDark(from));
  root.style.setProperty("--accent-tint-from", accentTint(from));
  root.style.setProperty("--accent-tint-to", accentTint(to));

  setWizardAccentProperties(to, from);
  themeOwnsWizardAccent = true;
};

export const applyThemeColors = (
  primary: string,
  secondary: string,
  buttonBackground: string,
  buttonText: string,
  accent?: IThemeAccent | null,
) => {
  const root = document.documentElement;
  const primaryHover = darkenHex(primary, 30);
  const buttonHover = darkenHex(buttonBackground, 30);

  root.style.setProperty("--background-primary-color", primary);
  root.style.setProperty("--background-primary-color-hover", primaryHover);
  root.style.setProperty("--background-secondary-color", secondary);
  root.style.setProperty("--button-background-color", buttonBackground);
  root.style.setProperty("--button-background-color-hover", buttonHover);
  root.style.setProperty("--button-text-color", buttonText);

  // Studio branding colors for background accents
  const primaryRgb = hexToRgb(primary);
  const secondaryRgb = hexToRgb(secondary);
  
  // Create rgba versions with different alpha values for layering
  root.style.setProperty("--studio-primary-alpha-05", `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.05)`);
  root.style.setProperty("--studio-primary-alpha-10", `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`);
  root.style.setProperty("--studio-primary-alpha-15", `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.15)`);
  root.style.setProperty("--studio-primary-alpha-20", `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.2)`);
  root.style.setProperty("--studio-primary-alpha-25", `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.25)`);
  root.style.setProperty("--studio-primary-alpha-30", `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.3)`);
  root.style.setProperty("--studio-primary-alpha-35", `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.35)`);
  root.style.setProperty("--studio-primary-alpha-45", `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.45)`);
  root.style.setProperty("--studio-primary-alpha-50", `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.5)`);
  
  root.style.setProperty("--studio-secondary-alpha-05", `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.05)`);
  root.style.setProperty("--studio-secondary-alpha-10", `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.1)`);
  root.style.setProperty("--studio-secondary-alpha-15", `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.15)`);
  root.style.setProperty("--studio-secondary-alpha-20", `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.2)`);
  root.style.setProperty("--studio-secondary-alpha-25", `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.25)`);
  root.style.setProperty("--studio-secondary-alpha-30", `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.3)`);
  root.style.setProperty("--studio-secondary-alpha-35", `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.35)`);
  root.style.setProperty("--studio-secondary-alpha-45", `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.45)`);
  root.style.setProperty("--studio-secondary-alpha-50", `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.5)`);

  applyAccentColors(accent);
};

/**
 * Returns either "#000" or "#fff" – whichever one
 * reaches the higher WCAG 2 contrast ratio against `bg`.
 */
export const readableText = (bg: string): "#000" | "#fff" => {
  /** Extract `[r,g,b]` 0-255 from hex / rgb / rgba input */
  const parse = (c: string): [number, number, number] => {
    if (c.startsWith("#")) {
      const hex =
        c.length === 4
          ? c.replace(/./g, (x) => x + x) // #rgb → #rrggbb
          : c;
      const int = parseInt(hex.slice(1), 16);
      return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
    }
    return (c.match(/\d+(\.\d+)?/g) || []).slice(0, 3).map(Number) as [
      number,
      number,
      number
    ];
  };

  /** sRGB → linear-RGB helper */
  const toLin = (v: number): number =>
    (v /= 255) <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;

  const [r, g, b] = parse(bg).map(toLin);
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b; // relative luminance

  const contrastWhite = 1.05 / (L + 0.05);
  const contrastBlack = (L + 0.05) / 0.05;

  return contrastWhite >= contrastBlack ? "#fff" : "#000";
};

export const generateRandomHexColor = (): string => {
  return Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, "0");
};

/**
 * Converts RGB to HSL
 */
const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

/**
 * Converts HSL to RGB
 */
const hslToRgb = (h: number, s: number, l: number): RGB => {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
};

/**
 * Brightens a hex color to ensure it's readable against dark backgrounds
 * Increases lightness to at least 72% and saturation to at least 65%
 */
export const brightenForDarkBackground = (hex: string): string => {
  hex = hex.replace("#", "");

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  let { h, s, l } = rgbToHsl(r, g, b);

  // Ensure minimum saturation for vibrancy
  if (s < 65) {
    s = 65;
  }

  // Ensure minimum lightness for readability
  if (l < 72) {
    l = 72;
  }

  const brightRgb = hslToRgb(h, s, l);
  return rgbToHex(brightRgb.r, brightRgb.g, brightRgb.b);
};

/**
 * Vivid take on a colour for small glowing markers on dark backgrounds (e.g. the
 * planner calendar's instructor dots). Unlike brightenForDarkBackground it keeps
 * colours saturated instead of pastel, and leaves greys grey rather than tinting
 * them red.
 */
export const vividForDarkBackground = (hex: string): string => {
  hex = hex.replace("#", "");

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  let { h, s, l } = rgbToHsl(r, g, b);

  // Only colours with a real hue get saturated; a grey has no hue to boost.
  if (s >= 10) {
    s = Math.max(s, 90);
  }

  // Light enough to glow on the dark UI, dark enough not to wash out.
  l = Math.min(Math.max(l, 62), 70);

  const vividRgb = hslToRgb(h, s, l);
  return rgbToHex(vividRgb.r, vividRgb.g, vividRgb.b);
};
